import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { Image } from 'expo-image';
import { getApiUrl } from '../utils/apiConfig';
import HeightPicklistDropdown from './HeightPicklistDropdown';
import {
  fetchExercisePerformanceHistory,
  formatSessionPerformanceHistoryLine,
} from '../utils/exercisePerformanceHistory';
import { weightStep } from '../utils/weightConversion';
import {
  addPerformanceSet,
  removePerformanceSet,
  copyFirstPerformanceSetToRemainingSets,
  updateSetRepCount,
  updateSetHoldSeconds,
  getSetDisplaySeconds,
  updateSetHeight,
  updateExerciseHeight,
  updateSetWeight,
  getSetDisplayWeight,
  clampInt,
  clampSeconds,
  isTimeBasedExercise,
  isHeightBasedExercise,
  isWeightBasedExercise,
  enrichExerciseWeightFlagFromCatalog,
} from '../utils/sessionExercisePerformance';

export const FIELD_LABEL_COLUMN_WIDTH = 86;
const STEPPER_BTN_SIZE = 32;
const STEPPER_CONTROL_GAP = 4;
const REPEAT_SETS_ICON = require('../../assets/repeat icon.png');

function FieldLabelColumn({ label, suffix, styles }) {
  if (!label && !suffix) return <View style={styles.fieldLabelColumn} />;
  return (
    <View style={styles.fieldLabelColumn}>
      <View style={styles.fieldLabelGroup}>
        {label ? (
          <Text
            style={styles.quantityLabel}
            {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseQuantityStepperText: 'true' } } : {})}
          >
            {label}
          </Text>
        ) : null}
        {suffix ? (
          <Text
            style={styles.quantitySuffixAfterLabel}
            {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseQuantityStepperText: 'true' } } : {})}
          >
            {suffix}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function StepperControls({ children, styles, dataSetKey }) {
  return (
    <View
      style={styles.quantityStepperControls}
      {...(Platform.OS === 'web' ? { dataSet: { [dataSetKey]: 'true' } } : {})}
    >
      {children}
    </View>
  );
}

function QuantityStepper({
  label,
  value,
  onChange,
  min = 1,
  styles,
  disabled = false,
  suffix,
  allowZero = false,
  stepperDataKey = 'sessionExerciseQuantityStepper',
}) {
  const floor = allowZero ? 0 : min;
  const n = allowZero ? clampSeconds(value, floor) : clampInt(value, min);
  const arrowColor = styles.__iconColors?.arrow ?? '#333333';

  const handleText = (text) => {
    const parsed = parseInt(String(text).replace(/\D/g, ''), 10);
    if (Number.isNaN(parsed)) return;
    onChange(allowZero ? clampSeconds(parsed, floor) : clampInt(parsed, min));
  };

  return (
    <View
      style={styles.fieldRow}
      {...(Platform.OS === 'web' ? { dataSet: { [stepperDataKey]: 'true' } } : {})}
    >
      <FieldLabelColumn label={label} suffix={suffix} styles={styles} />
      <StepperControls styles={styles} dataSetKey="sessionExerciseQuantityStepperControls">
        <TouchableOpacity
          style={[styles.quantityArrowBtn, (disabled || n <= floor) && styles.quantityArrowBtnDisabled]}
          onPress={() => onChange(n - 1)}
          disabled={disabled || n <= floor}
          accessibilityRole="button"
          accessibilityLabel={`Decrease ${label || 'value'}`}
        >
          <MaterialIcons name="remove" size={20} color={arrowColor} />
        </TouchableOpacity>
        <TextInput
          style={styles.quantityInput}
          value={String(n)}
          onChangeText={handleText}
          keyboardType="number-pad"
          editable={!disabled}
          selectTextOnFocus
          accessibilityLabel={label || 'Quantity'}
        />
        <TouchableOpacity
          style={[styles.quantityArrowBtn, disabled && styles.quantityArrowBtnDisabled]}
          onPress={() => onChange(n + 1)}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={`Increase ${label || 'value'}`}
        >
          <MaterialIcons name="add" size={20} color={arrowColor} />
        </TouchableOpacity>
      </StepperControls>
    </View>
  );
}

function WeightQuantityStepper({
  label,
  value,
  onChange,
  styles,
  disabled = false,
  suffix,
  weightInKg,
}) {
  const arrowColor = styles.__iconColors?.arrow ?? '#333333';
  const intervalRef = useRef(null);
  const fastRef = useRef(false);

  const clearRepeat = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    fastRef.current = false;
  };

  useEffect(() => () => clearRepeat(), []);

  const stepBy = (direction, fast = false) => {
    const step = weightStep(weightInKg, fast);
    const current = Number(value) || 0;
    const next = Math.max(0, Math.round((current + direction * step) * 100) / 100);
    onChange(next);
  };

  const startRepeat = (direction) => {
    if (disabled) return;
    clearRepeat();
    stepBy(direction, false);
    intervalRef.current = setInterval(() => {
      stepBy(direction, fastRef.current);
    }, fastRef.current ? 80 : 150);
  };

  const handleLongPress = (direction) => {
    fastRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => stepBy(direction, true), 80);
    }
  };

  const handleText = (text) => {
    const cleaned = String(text).replace(/[^\d.]/g, '');
    const parsed = parseFloat(cleaned);
    if (Number.isNaN(parsed)) {
      onChange(0);
      return;
    }
    onChange(Math.max(0, Math.round(parsed * 100) / 100));
  };

  const display = value != null && value !== '' ? String(value) : '0';

  return (
    <View
      style={styles.fieldRow}
      {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseWeightStepper: 'true' } } : {})}
    >
      <FieldLabelColumn label={label} suffix={suffix} styles={styles} />
      <StepperControls styles={styles} dataSetKey="sessionExerciseQuantityStepperControls">
        <TouchableOpacity
          style={[styles.quantityArrowBtn, disabled && styles.quantityArrowBtnDisabled]}
          onPressIn={() => startRepeat(-1)}
          onPressOut={clearRepeat}
          onLongPress={() => handleLongPress(-1)}
          delayLongPress={400}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={`Decrease ${label || 'weight'}`}
        >
          <MaterialIcons name="remove" size={20} color={arrowColor} />
        </TouchableOpacity>
        <TextInput
          style={styles.quantityInput}
          value={display}
          onChangeText={handleText}
          keyboardType="decimal-pad"
          editable={!disabled}
          selectTextOnFocus
          accessibilityLabel={label || 'Weight'}
        />
        <TouchableOpacity
          style={[styles.quantityArrowBtn, disabled && styles.quantityArrowBtnDisabled]}
          onPressIn={() => startRepeat(1)}
          onPressOut={clearRepeat}
          onLongPress={() => handleLongPress(1)}
          delayLongPress={400}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={`Increase ${label || 'weight'}`}
        >
          <MaterialIcons name="add" size={20} color={arrowColor} />
        </TouchableOpacity>
      </StepperControls>
    </View>
  );
}

export default function SessionExerciseSetsEditor({
  exercise,
  onExerciseChange,
  theme,
  studentId,
  weightInKg = true,
  readOnly = false,
  excludeTrainingSessionId = null,
}) {
  const styles = getStyles(theme);
  const iconColors = styles.__iconColors || {};
  const weightSuffix = weightInKg ? 'kg' : 'lbs';

  const [heightOptions, setHeightOptions] = useState([]);
  const [resolvedExercise, setResolvedExercise] = useState(exercise);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLines, setHistoryLines] = useState([]);
  const [historyError, setHistoryError] = useState(null);
  const historyCacheKeyRef = useRef('');

  useEffect(() => {
    setResolvedExercise(exercise);
    setHistoryOpen(false);
    setHistoryLines([]);
    setHistoryError(null);
    historyCacheKeyRef.current = '';
  }, [exercise?.id, exercise?.exercise_id]);

  useEffect(() => {
    if (!exercise?.exercise_id || isWeightBasedExercise(exercise)) return;
    let cancelled = false;
    void enrichExerciseWeightFlagFromCatalog(exercise, studentId).then((enriched) => {
      if (cancelled || !enriched || enriched === exercise) return;
      if (!isWeightBasedExercise(enriched)) return;
      setResolvedExercise(enriched);
      if (!readOnly) onExerciseChange(enriched);
    });
    return () => {
      cancelled = true;
    };
  }, [exercise, studentId, readOnly, onExerciseChange]);

  const activeExercise = resolvedExercise || exercise;
  const activePerformanceSets = activeExercise?.performance_sets || [];
  const activeTimeBased = isTimeBasedExercise(activeExercise);
  const activeHeightBased = isHeightBasedExercise(activeExercise);
  const activeWeightBased = isWeightBasedExercise(activeExercise);

  const loadPerformanceHistory = useCallback(async () => {
    if (!studentId || !activeExercise?.exercise_id) {
      setHistoryLines([]);
      setHistoryError('No exercise history available.');
      return;
    }
    const cacheKey = `${studentId}:${activeExercise.exercise_id}:${excludeTrainingSessionId || ''}`;
    if (historyCacheKeyRef.current === cacheKey) {
      return;
    }
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const rows = await fetchExercisePerformanceHistory({
        studentId,
        exerciseId: activeExercise.exercise_id,
        limit: 3,
        excludeSessionId: excludeTrainingSessionId,
      });
      setHistoryLines(
        rows.map((row) => formatSessionPerformanceHistoryLine(row, weightInKg))
      );
      historyCacheKeyRef.current = cacheKey;
    } catch {
      setHistoryError('Could not load exercise history.');
      setHistoryLines([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [studentId, activeExercise, excludeTrainingSessionId, weightInKg]);

  const togglePerformanceHistory = () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    void loadPerformanceHistory();
  };

  const loadHeightOptions = useCallback(async () => {
    if (!studentId || !activeHeightBased) return;
    try {
      const res = await fetch(
        `${getApiUrl()}/api/height-picklist-options?student_id=${encodeURIComponent(String(studentId))}`
      );
      if (res.ok) {
        const data = await res.json();
        setHeightOptions(Array.isArray(data) ? data : []);
      }
    } catch {
      /* ignore */
    }
  }, [studentId, activeHeightBased]);

  useEffect(() => {
    void loadHeightOptions();
  }, [loadHeightOptions]);

  const addHeightOption = async (label) => {
    const res = await fetch(`${getApiUrl()}/api/height-picklist-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ student_id: studentId, height_label: label }),
    });
    if (!res.ok) throw new Error('Failed to save height');
    await loadHeightOptions();
    return res.json();
  };

  if (!activeExercise) return null;

  const apply = (next) => {
    if (!readOnly) {
      setResolvedExercise(next);
      onExerciseChange(next);
    }
  };

  const heightDropdownProps = {
    inlineLabelWidth: FIELD_LABEL_COLUMN_WIDTH,
    theme,
    disabled: readOnly,
    options: heightOptions,
    onAddOption: addHeightOption,
  };

  return (
    <View
      style={styles.setsEditor}
      {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseSetsEditor: 'true' } } : {})}
    >
      <Text style={styles.setsEditorTitle}>Sets & reps</Text>

      {activeHeightBased ? (
        <View style={styles.setFieldRow}>
          <HeightPicklistDropdown
            label="Height"
            value={activeExercise.height}
            onSelect={(h) => apply(updateExerciseHeight(activeExercise, h))}
            {...heightDropdownProps}
          />
        </View>
      ) : null}

      {activePerformanceSets.map((setRow, setIndex) => (
        <View key={setRow.client_id} style={styles.setBoxRow}>
          <View
            style={styles.setBox}
            {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseSetBlock: 'true' } } : {})}
          >
            <Text style={styles.setBoxTitle}>Set {setRow.set_number}</Text>

            {activeHeightBased ? (
              <View style={styles.setFieldRow}>
                <HeightPicklistDropdown
                  label="Height"
                  value={setRow.height}
                  onSelect={(h) => apply(updateSetHeight(activeExercise, setRow.client_id, h))}
                  {...heightDropdownProps}
                />
              </View>
            ) : null}

            <View style={styles.setFieldRow}>
              {activeTimeBased ? (
                <QuantityStepper
                  label="Seconds"
                  suffix="sec"
                  value={getSetDisplaySeconds(setRow, activeExercise)}
                  onChange={(sec) => apply(updateSetHoldSeconds(activeExercise, setRow.client_id, sec))}
                  styles={styles}
                  allowZero
                  min={0}
                  disabled={readOnly}
                />
              ) : (
                <QuantityStepper
                  label="Reps"
                  value={setRow.prescribed_reps}
                  onChange={(count) => apply(updateSetRepCount(activeExercise, setRow.client_id, count))}
                  styles={styles}
                  disabled={readOnly}
                />
              )}
            </View>

            {activeWeightBased ? (
              <View style={styles.setFieldRow}>
                <WeightQuantityStepper
                  label="Weight"
                  value={getSetDisplayWeight(setRow, weightInKg)}
                  onChange={(w) =>
                    apply(updateSetWeight(activeExercise, setRow.client_id, w, weightInKg))
                  }
                  styles={styles}
                  suffix={weightSuffix}
                  weightInKg={weightInKg}
                  disabled={readOnly}
                />
              </View>
            ) : null}
          </View>

          {!readOnly ? (
            <View style={styles.setActionsCol}>
              <TouchableOpacity
                style={styles.setDeleteBtn}
                onPress={() => apply(removePerformanceSet(activeExercise, setRow.client_id))}
                accessibilityRole="button"
                accessibilityLabel={`Remove set ${setRow.set_number}`}
                {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseSetDelete: 'true' } } : {})}
              >
                <MaterialIcons name="delete-outline" size={22} color={iconColors.delete} />
              </TouchableOpacity>
              {setIndex === 0 && activePerformanceSets.length > 1 ? (
                <TouchableOpacity
                  style={styles.setRepeatBtn}
                  onPress={() => apply(copyFirstPerformanceSetToRemainingSets(activeExercise))}
                  accessibilityRole="button"
                  accessibilityLabel="Copy set 1 values to all other sets"
                  {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseSetRepeat: 'true' } } : {})}
                >
                  <Image
                    source={REPEAT_SETS_ICON}
                    style={styles.setRepeatIcon}
                    contentFit="contain"
                    {...(Platform.OS !== 'web' ? { accessibilityIgnoresInvertColors: true } : {})}
                  />
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
        </View>
      ))}

      {(studentId && activeExercise?.exercise_id) || !readOnly ? (
        <View style={styles.setActionsRow}>
          {studentId && activeExercise?.exercise_id ? (
            <TouchableOpacity
              style={styles.historyBtn}
              onPress={togglePerformanceHistory}
              accessibilityRole="button"
              accessibilityLabel={
                historyOpen ? 'Hide recent exercise history' : 'Show recent exercise history'
              }
              accessibilityState={{ expanded: historyOpen }}
              {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseHistoryBtn: 'true' } } : {})}
            >
              <MaterialIcons
                name="history"
                size={22}
                color={historyOpen ? iconColors.add : iconColors.arrow}
              />
            </TouchableOpacity>
          ) : null}
          {!readOnly ? (
            <TouchableOpacity
              style={styles.addSetBtn}
              onPress={() => apply(addPerformanceSet(activeExercise))}
            >
              <MaterialIcons name="add-circle-outline" size={22} color={iconColors.add} />
              <Text style={styles.addSetBtnText}>Add set</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {historyOpen ? (
        <View
          style={styles.historyPanel}
          {...(Platform.OS === 'web' ? { dataSet: { sessionExerciseHistoryPanel: 'true' } } : {})}
        >
          <Text style={styles.historyPanelTitle}>Recent sessions</Text>
          {historyLoading ? (
            <Text style={styles.historyPanelText}>Loading…</Text>
          ) : historyError ? (
            <Text style={styles.historyPanelText}>{historyError}</Text>
          ) : historyLines.length ? (
            historyLines.map((line, index) => (
              <Text key={`history-${index}`} style={styles.historyLine}>
                {line}
              </Text>
            ))
          ) : (
            <Text style={styles.historyPanelText}>No previous sessions logged for this exercise.</Text>
          )}
        </View>
      ) : null}
    </View>
  );
}

const getStyles = (theme) => {
  const arrowColor = theme.isDarkMode ? '#FFFFFF' : '#333333';
  const deleteColor = theme.isDarkMode ? '#EF9A9A' : '#C62828';
  const addColor = theme.colors.primary || '#d4af37';
  const sheet = StyleSheet.create({
    setsEditor: {
      marginTop: 8,
      paddingTop: 8,
      paddingHorizontal: 8,
      paddingBottom: 4,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#faf8f4',
      borderRadius: 8,
    },
    setsEditorTitle: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    setBoxRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 10,
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    setBox: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      borderRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#ffffff',
    },
    setBoxTitle: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    setFieldRow: {
      marginBottom: 6,
      width: '100%',
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    fieldRow: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    fieldLabelColumn: {
      width: FIELD_LABEL_COLUMN_WIDTH,
      flexShrink: 0,
      backgroundColor: 'transparent',
    },
    fieldLabelGroup: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      backgroundColor: 'transparent',
    },
    quantityLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    quantitySuffixAfterLabel: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.85,
      marginLeft: 4,
      backgroundColor: 'transparent',
    },
    quantityStepperControls: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' ? { maxWidth: '100%' } : {}),
    },
    quantityArrowBtn: {
      width: STEPPER_BTN_SIZE,
      height: STEPPER_BTN_SIZE,
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#EEEEEE',
    },
    quantityArrowBtnDisabled: { opacity: 0.35 },
    quantityInput: {
      flex: 1,
      minWidth: 0,
      textAlign: 'center',
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#CCCCCC',
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 2,
      marginHorizontal: STEPPER_CONTROL_GAP,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
    },
    setDeleteBtn: {
      padding: 6,
      backgroundColor: 'transparent',
    },
    setActionsCol: {
      marginLeft: 8,
      marginTop: 4,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    setRepeatBtn: {
      padding: 6,
      marginTop: 2,
      backgroundColor: 'transparent',
    },
    setRepeatIcon: {
      width: 22,
      height: 22,
    },
    addSetBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 4,
      backgroundColor: 'transparent',
    },
    setActionsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    historyBtn: {
      paddingVertical: 4,
      paddingRight: 14,
      backgroundColor: 'transparent',
    },
    historyPanel: {
      marginTop: 8,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      backgroundColor: 'transparent',
    },
    historyPanelTitle: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      opacity: 0.85,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    historyPanelText: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.75,
      backgroundColor: 'transparent',
    },
    historyLine: {
      fontSize: 13,
      color: theme.colors.text,
      lineHeight: 20,
      marginBottom: 2,
      backgroundColor: 'transparent',
    },
    addSetBtnText: {
      marginLeft: 4,
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
  });
  return {
    ...sheet,
    __iconColors: { arrow: arrowColor, delete: deleteColor, add: addColor },
  };
};
